import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import "../../assets/css/legal.css";

const sections = [
  {
    id: "info-collect",
    num: "01",
    icon: "bi-database",
    heading: "Information We Collect",
    content: (
      <>
        <p>We may collect the following types of information when you use Zoomey:</p>
        <ul>
          <li><strong>Personal Information:</strong> Your name, email address, and profile details you provide during registration.</li>
          <li><strong>Chat Data:</strong> Messages and interactions you have with Zoomey, stored to improve the assistant's functionality and responses.</li>
          <li><strong>Usage Data:</strong> Timestamps, device information, browser type, and interaction patterns to help us improve performance.</li>
        </ul>
      </>
    ),
  },
  {
    id: "how-we-use",
    num: "02",
    icon: "bi-gear",
    heading: "How We Use Your Information",
    content: (
      <>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide, maintain, and improve Zoomey's services.</li>
          <li>Personalize your experience and tailor AI responses to your needs.</li>
          <li>Analyze usage patterns to improve platform performance.</li>
          <li>Communicate with you regarding updates, features, or support.</li>
        </ul>
      </>
    ),
  },
  {
    id: "data-sharing",
    num: "03",
    icon: "bi-shield-lock",
    heading: "Data Sharing & Security",
    content: (
      <>
        <p>We do not sell your personal information. We share your data only with the service providers needed to operate Zoomey — including the third-party AI providers described below — or when required by law.</p>
        <div className="legal-highlight">
          <i className="bi bi-shield-check"></i>
          We use industry-standard encryption and security measures to protect your data from unauthorized access, disclosure, or misuse, and we require our service providers to protect your data to the same standard described in this policy.
        </div>
      </>
    ),
  },
  {
    id: "third-party-ai",
    num: "04",
    icon: "bi-cpu",
    heading: "Third-Party AI Services",
    content: (
      <>
        <p>Zoomey AI Assistant is powered by third-party AI providers. When you interact with the assistant, we send the following to these providers to generate a response:</p>
        <ul>
          <li><strong>Data sent:</strong> your chat messages, voice recordings, images from camera or screen sharing during a session, files you attach, and the conversation context needed to generate a response.</li>
          <li><strong>Who receives it:</strong> Anthropic (Claude), OpenAI, and Google (Gemini).</li>
          <li><strong>Connected accounts:</strong> if you choose to connect services such as Gmail, Google Calendar, Google Drive, Notion, HubSpot, or Fireflies, the relevant content from those accounts is shared to fulfill the requests you make.</li>
          <li><strong>Purpose:</strong> solely to generate assistant responses and power voice conversations. Your content is not used for advertising.</li>
          <li><strong>Your choice:</strong> we ask for your permission before sharing any content with AI providers, and you can change your choice at any time in the app's settings.</li>
        </ul>
        <p>These AI providers are contractually required to protect your data with the same or equal protection as described in this policy.</p>
      </>
    ),
  },
  {
    id: "your-rights",
    num: "05",
    icon: "bi-person-check",
    heading: "Your Rights",
    content: (
      <>
        <p>You have the right to:</p>
        <ul>
          <li>Access the personal information we have collected about you.</li>
          <li>Request corrections to inaccurate or incomplete data.</li>
          <li>Request the deletion of your personal information.</li>
          <li>Opt-out of certain data collection practices.</li>
        </ul>
        <p>To exercise any of these rights, contact us at <a href="mailto:privacy@playces.in">privacy@playces.in</a>.</p>
      </>
    ),
  },
  {
    id: "cookies",
    num: "06",
    icon: "bi-cookie",
    heading: "Cookies",
    content: (
      <>
        <p>Zoomey uses cookies to:</p>
        <ul>
          <li>Remember your theme preference (dark/light mode).</li>
          <li>Maintain your login session securely.</li>
          <li>Store sidebar and UI preferences.</li>
        </ul>
        <p>You can disable cookies in your browser settings, though some features may not function correctly.</p>
      </>
    ),
  },
  {
    id: "changes",
    num: "07",
    icon: "bi-arrow-repeat",
    heading: "Changes to This Policy",
    content: (
      <>
        <p>We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons.</p>
        <p>Any changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically.</p>
      </>
    ),
  },
];

const PrivacyPolicy = () => {
  const [activeId, setActiveId] = useState(sections[0].id);
  const scrollRef = useRef(null);
  const isDarkMode = Cookies.get("isDarkMode") === "true";

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { root: container, rootMargin: "-10% 0px -40% 0px", threshold: 0 }
    );
    sections.forEach(({ id }) => {
      const el = container.querySelector(`#${id}`);
      if (el) observer.observe(el);
    });

    // Activate last section when scrolled to bottom
    const lastId = sections[sections.length - 1].id;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight - 40) {
        setActiveId(lastId);
      }
    };
    container.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      observer.disconnect();
      container.removeEventListener("scroll", onScroll);
    };
  }, []);

  const scrollTo = (id) => {
    const container = scrollRef.current;
    const el = container?.querySelector(`#${id}`);
    if (!el || !container) return;
    container.scrollTo({ top: el.offsetTop - 24, behavior: "smooth" });
  };

  return (
    <div className="legal-page">

      {/* ── Top Nav ── */}
      <nav className="legal-topbar">
        <div className="legal-topbar-inner">
          <Link to="/login" className="zoomey-logo-wrapper">
            <div className={`zoomey-logo-mark ${isDarkMode ? "dark" : "light"}`}>
              <div className="logo-glow"></div>
              <div className="logo-inner">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 7H22L8 21H22" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="z-path" />
                  <circle cx="22" cy="7" r="2" fill="currentColor" className="z-dot" />
                </svg>
              </div>
            </div>
            <div className="zoomey-brand-text">
              <span className={`brand-name ${isDarkMode ? "dark" : "light"}`}>ZOOMEY</span>
            </div>
          </Link>
          <div className="legal-topbar-right">
            <Link to="/pricing" className="legal-topbar-link">Pricing</Link>
            <Link to="/faq" className="legal-topbar-link">FAQ</Link>
            <Link to="/terms-and-conditions" className="legal-topbar-link">Terms &amp; Conditions</Link>
          </div>
        </div>
      </nav>

      {/* ── Main Layout ── */}
      <div className="legal-layout">

        {/* LEFT — Fixed Sidebar */}
        <aside className="legal-sidebar">
          <p className="legal-sidebar-label">On this page</p>
          <nav className="legal-sidebar-nav">
            {sections.map(({ id, num, heading, icon }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`legal-nav-item ${activeId === id ? "active" : ""}`}
              >
                <span className="legal-nav-num">{num}</span>
                <span className="legal-nav-text">{heading}</span>
                {activeId === id && <span className="legal-nav-dot"></span>}
              </button>
            ))}
          </nav>

          <div className="legal-sidebar-contact">
            <p className="legal-sidebar-contact-label">Questions?</p>
            <a href="mailto:privacy@playces.in" className="legal-sidebar-contact-link">
              <i className="bi bi-envelope"></i>
              privacy@playces.in
            </a>
          </div>
        </aside>

        {/* RIGHT — Scrollable */}
        <div className="legal-right-scroll" ref={scrollRef}>

          {/* Hero */}
          <header className="legal-hero">
            <div className="legal-hero-bg-grid"></div>
            <div className="legal-hero-inner">
              <div className="legal-hero-badge">
                <i className="bi bi-shield-check"></i>
                Privacy Policy
              </div>
              <h1 className="legal-hero-title">How we handle your data</h1>
              <p className="legal-hero-sub">
                We believe in full transparency. Here's exactly what we collect,
                why we collect it, and how we protect it.
              </p>
              <div className="legal-hero-chips">
                <div className="legal-chip">
                  <i className="bi bi-calendar-check"></i>
                  Effective: April 6, 2026
                </div>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="legal-content">
            <div className="legal-intro-box">
              <div className="legal-intro-icon"><i className="bi bi-info-circle"></i></div>
              <div>
                Welcome to Zoomey. Your privacy is important to us and we are committed to
                protecting your personal information. This policy is operated by{" "}
                <strong>Playces.in</strong> and applies alongside our{" "}
                <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>.
              </div>
            </div>

            {sections.map(({ id, num, icon, heading, content }) => (
              <section key={id} id={id} className="legal-section">
                <div className="legal-section-header">
                  <div className="legal-section-num">{num}</div>
                  <div className="legal-section-title-wrap">
                    <div className="legal-section-icon-box">
                      <i className={`bi ${icon}`}></i>
                    </div>
                    <h2 className="legal-section-title">{heading}</h2>
                  </div>
                </div>
                <div className="legal-section-body">{content}</div>
              </section>
            ))}

            <div className="legal-last-updated">
              <i className="bi bi-patch-check"></i>
              <span>Last reviewed on <strong>April 6, 2026</strong>. Previous versions available upon request.</span>
            </div>
          </main>

          {/* Footer */}
          <footer className="legal-footer">
            <div className="legal-footer-inner">
              <div className="legal-footer-left">
                <span className="legal-footer-brand">ZOOMEY</span>
                <span>© 2026 Playces.in. All rights reserved.</span>
              </div>
              <div className="legal-footer-links">
                <Link to="/pricing">Pricing</Link>
                <Link to="/faq">FAQ</Link>
                <Link to="/privacy-policy">Privacy Policy</Link>
                <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>
                <a href="mailto:privacy@playces.in">Contact</a>
                <Link to="/login">Sign In</Link>
              </div>
            </div>
          </footer>

        </div>{/* end .legal-right-scroll */}
      </div>{/* end .legal-layout */}

    </div>
  );
};

export default PrivacyPolicy;
